// Anzeige-Bausteine eines Auftrags im Dokumenten-Assistenten (seit 21.10.2026):
// erkannte Dokumente, Ernsthaftigkeitsnachweis, Phase, Seiten im Einzelnen und
// Protokoll. Die Seite (AuftragKarte.jsx) und die Testkarte der Automatik in der
// Admin-Uebersicht (settings/DokumentenAutomatikTest.jsx) zeigen dasselbe.

import React, { useState } from 'react';
import { T, markeStil } from '../../../lib/designTokens';
import {
  TOENE, jaNein, nachweisText, phaseAussage, seitenArtText, seitenText, statusAnzeige, zeitText,
} from '../../../lib/dokumentenAssistent';
import { Aufklapper, Ueberzeile } from '../abwaegenAnzeige';

const zelle = {
  padding: '7px 10px', fontSize: T.tSm, color: T.ink, textAlign: 'left',
  borderBottom: `1px solid ${T.hairline}`, verticalAlign: 'top',
};
const kopfZelle = {
  ...zelle, fontSize: T.tMikro, fontWeight: 700, color: T.muted,
  background: T.grund, borderBottom: `1px solid ${T.randWeich}`,
};
const tabellenRahmen = {
  border: `1px solid ${T.randWeich}`, borderRadius: T.rKarte, overflowX: 'auto', background: T.flaeche,
};

/** Statusmarke; art: 'auftrag' | 'dokument' | 'nachweis'. */
export function StatusMarke({ status, art = 'auftrag' }) {
  const anzeige = statusAnzeige(status, art);
  return <span style={markeStil(anzeige.farbe, anzeige.rand, anzeige.grund)}>{anzeige.text}</span>;
}

/** Hinweisleiste mit farbigem Strich links - fuer Fehler und Warnungen. */
export function Hinweis({ ton = 'gefahr', children }) {
  const farbe = TOENE[ton] || TOENE.gefahr;
  return (
    <div role={ton === 'gefahr' ? 'alert' : undefined} style={{
      padding: '9px 12px', borderRadius: T.rChip, background: farbe.grund,
      borderLeft: `3px solid ${farbe.farbe}`, color: farbe.farbe, fontSize: T.tMeta, fontWeight: 600, lineHeight: 1.5,
    }}>{children}</div>
  );
}

/** Die erkannten Dokumente: Dokument, Seiten, Status, Grund bzw. Dateiname. */
export function DokumenteTabelle({ dokumente }) {
  const liste = Array.isArray(dokumente) ? dokumente : [];
  if (liste.length === 0) {
    return <div style={{ fontSize: T.tMeta, color: T.muted }}>Keine Dokumente erkannt.</div>;
  }
  return (
    <div style={tabellenRahmen}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
        <thead>
          <tr>
            <th style={kopfZelle}>Dokument</th>
            <th style={kopfZelle}>Seiten</th>
            <th style={kopfZelle}>Status</th>
            <th style={kopfZelle}>Hinweis</th>
          </tr>
        </thead>
        <tbody>
          {liste.map((dokument, index) => (
            <tr key={`${dokument.typ || dokument.label}-${index}`}>
              <td style={{ ...zelle, fontWeight: 600 }}>{dokument.label || dokument.typ || 'Dokument'}</td>
              <td style={{ ...zelle, whiteSpace: 'nowrap', color: T.body }}>{seitenText(dokument.seiten) || '–'}</td>
              <td style={zelle}><StatusMarke status={dokument.status} art="dokument" /></td>
              <td style={{ ...zelle, fontSize: T.tMikro, color: dokument.status === 'fehler' ? T.gefahr : T.body, lineHeight: 1.45 }}>
                {dokument.grund || dokument.dateiname || '–'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Zeile "Ernsthaftigkeitsnachweis" - Seite 1 und die Unterschriftsseite als eine PDF. */
export function NachweisZeile({ nachweis }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', fontSize: T.tMeta, color: T.body }}>
      <span style={{ fontWeight: 700, color: T.ink }}>Ernsthaftigkeitsnachweis</span>
      {nachweis?.status ? <StatusMarke status={nachweis.status} art="nachweis" /> : null}
      <span style={{ lineHeight: 1.5 }}>{nachweisText(nachweis)}</span>
    </div>
  );
}

/** Was mit der Phase des Deals geschah (Automatik) oder geschehen wuerde (Probelauf). */
export function PhaseZeile({ auftrag }) {
  const aussage = phaseAussage(auftrag);
  if (!aussage) return null;
  const farbe = TOENE[aussage.ton] || TOENE.leise;
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', fontSize: T.tMeta }}>
      <span style={{ fontWeight: 700, color: T.ink }}>Phase</span>
      <span style={{ color: farbe.farbe, fontWeight: 600, lineHeight: 1.5 }}>{aussage.text}</span>
    </div>
  );
}

/**
 * Unterschriften gibt es nur auf Seiten mit Hauptunterschrift - dort hat der Server sie eigens
 * zweimal geprueft. Auf allen anderen Seiten steht "–" statt eines nichtssagenden "nein".
 */
function unterschriftZelle(seite, wert) {
  return seite.hauptunterschrift ? jaNein(wert) : '–';
}

function SeitenTabelle({ seiten }) {
  return (
    <div style={tabellenRahmen}>
      <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
        <thead>
          <tr>
            <th style={kopfZelle}>Seite</th>
            <th style={kopfZelle}>Erkannt als</th>
            <th style={kopfZelle} title="Hauptunterschriftenblock des Vertrags">Hauptunterschrift</th>
            <th style={kopfZelle} title="Unterschrift des Grundstückseigentümers – eigens zweimal geprüft, nur ja, wenn beide Male ja">Kunde</th>
            <th style={kopfZelle} title="Unterschrift der Anlagenbetreiberin – eigens zweimal geprüft, nur ja, wenn beide Male ja">ENPOWER</th>
            <th style={kopfZelle}>Begründung der KI</th>
          </tr>
        </thead>
        <tbody>
          {seiten.map((seite, index) => (
            <tr key={`${seite.nr}-${index}`}>
              <td style={{ ...zelle, fontWeight: 700 }}>{seite.nr}</td>
              <td style={{ ...zelle, color: seite.art ? T.ink : T.warn }}>{seitenArtText(seite.art)}</td>
              <td style={zelle}>{jaNein(seite.hauptunterschrift)}</td>
              <td style={zelle}>{unterschriftZelle(seite, seite.unterschriftKunde)}</td>
              <td style={zelle}>{unterschriftZelle(seite, seite.unterschriftEnpower)}</td>
              <td style={{ ...zelle, fontSize: T.tMikro, color: T.body, lineHeight: 1.45 }}>
                {seite.grund || '–'}
                {seite.unterschriftHinweis ? (
                  <div style={{ color: T.warn, fontWeight: 600, marginTop: 3 }}>{seite.unterschriftHinweis}</div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProtokollListe({ protokoll }) {
  return (
    <div style={{
      border: `1px solid ${T.randWeich}`, borderRadius: T.rKarte, background: T.grund,
      padding: '8px 10px', display: 'grid', gap: 4, maxHeight: 260, overflowY: 'auto',
    }}>
      {protokoll.map((eintrag, index) => (
        <div key={`${eintrag.zeit}-${index}`} style={{ display: 'flex', gap: 10, fontSize: T.tMikro, lineHeight: 1.5 }}>
          <span style={{ color: T.label, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
            {zeitText(eintrag.zeit) || '–'}
          </span>
          <span style={{ color: T.body }}>{eintrag.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Aufklappbar: "Seiten im Einzelnen" und "Protokoll". */
export function AuftragAufklapper({ auftrag }) {
  const [offen, setOffen] = useState('');
  const seiten = Array.isArray(auftrag?.seiten) ? auftrag.seiten : [];
  const protokoll = Array.isArray(auftrag?.protokoll) ? auftrag.protokoll : [];
  if (seiten.length === 0 && protokoll.length === 0) return null;
  const umschalten = (was) => setOffen((alt) => (alt === was ? '' : was));
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {seiten.length ? (
          <Aufklapper offen={offen === 'seiten'} onClick={() => umschalten('seiten')}>
            Seiten im Einzelnen ({seiten.length})
          </Aufklapper>
        ) : null}
        {protokoll.length ? (
          <Aufklapper offen={offen === 'protokoll'} onClick={() => umschalten('protokoll')}>
            Protokoll ({protokoll.length})
          </Aufklapper>
        ) : null}
      </div>
      {offen === 'seiten' ? <SeitenTabelle seiten={seiten} /> : null}
      {offen === 'protokoll' ? <ProtokollListe protokoll={protokoll} /> : null}
    </div>
  );
}

/** Ergebnis eines Auftrags: Dokumente, Nachweis, Phase (nur wenn gefragt), Aufklapper. */
export function AuftragErgebnis({ auftrag, mitPhase = false }) {
  if (!auftrag) return null;
  const hatDokumente = Array.isArray(auftrag.dokumente) && auftrag.dokumente.length > 0;
  // Nach Fehler oder Liegenlassen gibt es meist nichts Erkanntes - dann keine leere Tabelle.
  const fertig = auftrag.status === 'fertig' || auftrag.status === 'teilweise';
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {hatDokumente || fertig ? (
        <div style={{ display: 'grid', gap: 6 }}>
          <Ueberzeile>Erkannte Dokumente</Ueberzeile>
          <DokumenteTabelle dokumente={auftrag.dokumente} />
        </div>
      ) : null}
      {auftrag.nachweis || fertig ? <NachweisZeile nachweis={auftrag.nachweis} /> : null}
      {mitPhase ? <PhaseZeile auftrag={auftrag} /> : null}
      <AuftragAufklapper auftrag={auftrag} />
    </div>
  );
}
